import React, { useLayoutEffect, useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import {
	LoadingOverlay,
	TextInput,
	Title,
	Text,
	useMantineTheme,
	PasswordInput,
	Checkbox,
	Notification,
	Button,
	Box,
} from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import { useSearchParams } from 'react-router-dom';
import { AppPaths } from '@/configs/appPaths';
import { useContent } from '@/providers/ContentProvider';
import { isAppError } from '@/utils/utilities';
import { HeroImg } from '@/components/partials/heroImg/HeroImg';
import { useMutation } from '@tanstack/react-query';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import styles from './Login.module.scss';
import { ErrorCode } from '@/types/types';

export interface LoginValues {
	email: string;
	password: string;
}

export enum SubmitStatus {
	THROTTLE,
	LOADING,
	ERROR,
	SUCCESS,
}

const initialValues = {
	email: '',
	password: '',
};

export type AvailableSocials = 'google' | 'facebook';

const baseSlug = 'login.';

export const Login: React.FC = () => {
	const { getErrorMessage, getContent } = useContent();

	const { other } = useMantineTheme();
	const { remember, setRemember, login } = useAuth();

	const [status, setStatus] = useState(SubmitStatus.THROTTLE);
	const [resultMessage, setResultMessage] = useState('');

	const [searchParams, setSearchParams] = useSearchParams();

	const form = useForm({
		initialValues,
		validate: {
			email: isEmail(getErrorMessage([ErrorCode.INVALID_EMAIL])),
			password: (value) =>
				!value ? getErrorMessage([ErrorCode.GENERIC]) : null,
		},
	});

	const verifyRegistrationMutation = useMutation({
		mutationFn: UnauthenticatedApi.verifyEmailToken,
		onSuccess: () => {
			setSearchParams({});
			setStatus(SubmitStatus.SUCCESS);
			setResultMessage(getContent(`${baseSlug}verifyEmailSuccess`));
		},
		onError: (error) => {
			const isError = isAppError(error);
			if (!isError) return;

			setStatus(SubmitStatus.ERROR);
			setResultMessage(getErrorMessage(error.stack));
		},
	});

	useLayoutEffect(() => {
		const hash = searchParams.get('token');
		if (!hash) return;

		verifyRegistrationMutation.mutate(hash);
	}, []);

	const handleSubmit = form.onSubmit(async (loginValues: LoginValues) => {
		setStatus(SubmitStatus.LOADING);
		setResultMessage('');

		const result = await login(loginValues);

		form.reset();

		if (result.status === SubmitStatus.ERROR) {
			setStatus(SubmitStatus.ERROR);
			setResultMessage(getErrorMessage(result.stack));
			return;
		}

		setStatus(SubmitStatus.THROTTLE);
	});

	return (
		<div className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={handleSubmit}>
				<LoadingOverlay visible={status === SubmitStatus.LOADING} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}header`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}subHeader`)}
				</Text>
				{status === SubmitStatus.ERROR && (
					<Notification {...other.notificationProps.danger} mb="lg">
						<Text size="md">{resultMessage}</Text>
					</Notification>
				)}
				{status === SubmitStatus.SUCCESS && (
					<Notification {...other.notificationProps.success} mb="lg">
						<Text size="md">{resultMessage}</Text>
					</Notification>
				)}
				<TextInput
					{...form.getInputProps('email')}
					required
					type="email"
					placeholder={getContent(`${baseSlug}inputEmailPlaceholder`)}
					mb="lg"
				/>
				<PasswordInput
					{...form.getInputProps('password')}
					required
					placeholder={getContent(`${baseSlug}inputPasswordPlaceholder`)}
					mb="lg"
				/>
				<Checkbox
					label={getContent(`${baseSlug}checkboxRemember`)}
					mb="lg"
					checked={remember}
					onChange={({ target }) => setRemember(target.checked)}
				/>
				<Button type="submit" mb="lg" w="100%">
					{getContent(`${baseSlug}submitBtn`)}
				</Button>
				<Box ta="center" c="dimmed" mb="md">
					{getContent(`${baseSlug}navigateReset`, {
						links: {
							$RESET_PASSWORD: {
								to: `/${AppPaths.RESET_PASSWORD}`,
								className: 'injected-anchor',
							},
						},
					})}
				</Box>
			</form>
			<HeroImg src={getContent(`${baseSlug}heroImg`)} />
		</div>
	);
};
